import {Component, EventEmitter, OnDestroy, OnInit, Output} from '@angular/core';
import {Router} from '@angular/router';
import {EntitySchemaService} from '@catcore/services/entity-schema.service';
import {DefaultEntityLanding} from '@catcore/shared/default-entity-landing';
import {GenericLinkedResource} from '@catcore/types/base';
import {extractDataFromModel, genericResourceToLinkItem} from '@catcore/utils';
import {GenericResourceService} from '@catfeatures/services/generic-resource.service';
import {ProjectFeatureService} from '@catmodules/models/project-feature/services/project-feature.service';
import {ProjectFeature} from '@catmodules/models/project-feature/types';
import {ModifyUnitComponent} from '@catmodules/models/units/components/modify-unit/modify-unit.component';
import {ModalService} from '@catmodules/widgets/slide-in-modal/services/modal.service';
import {LinkItem} from '@catui/types';
import {Subscription} from 'rxjs/Subscription';
import {isNullOrUndefined} from 'util';
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {ModifyServiceComponent} from "@catmodules/models/services/components/modify-service/modify-service.component";
import {ConfirmationService, SelectItem} from "primeng/primeng";
import {NotificationsService, ProjectService} from "@catcore/services";
import {UpdateService} from "@catcore/services/update.service";

const EntitySchemaTypeRef = 'ProjectUnits';
const selfTitle = 'Project Units';

@Component({
  templateUrl: './project-features-landing.component.html',
})
export class ProjectFeaturesLandingComponent extends DefaultEntityLanding<ProjectFeature> implements OnInit, OnDestroy {

  linkedUnits: LinkItem[] = [];
  linkedServices: LinkItem[] = [];
  unitSubscription: Subscription;
  private projectSubscription: Subscription;
  private updateSubscription: Subscription;

  @Output() onFormSubmit = new EventEmitter();
  @Output() deleteResource = new EventEmitter();

  form: FormGroup;

  private currentResourceService;
  private feature;

  guiZoneSelectItems: SelectItem[] = [];

  constructor(
    schemaService: EntitySchemaService,
    private featureService: ProjectFeatureService,
    private genericService: GenericResourceService,
    private modalService: ModalService,
    private projectService: ProjectService,
    private updateService: UpdateService,
    private router: Router
  ) {
    super(schemaService);
    this.setSelfTitle(selfTitle);
  }

  ngOnInit() {
    this.projectSubscription = this.projectService
      .getCurrentProject()
      .subscribe((project) => this.updateGuiZoneSelectItems(project));
  }

  ngOnDestroy() {
    this.projectSubscription.unsubscribe();
    if (this.updateSubscription) {
      this.updateSubscription.unsubscribe();
    }
    if (this.unitSubscription) {
      this.unitSubscription.unsubscribe();
    }
  }

  protected onEntityReceived(feature: ProjectFeature) {
    this.feature = feature;
    const units = this.featureService.extractUnitsFor(feature);
    this.linkedUnits = genericResourceToLinkItem(units, (value: GenericLinkedResource) => (router: Router) => this.openModifyUnitDialog(value));
    this.currentResourceService = this.genericService.findServiceForResource(this.resourceUri);

    const services = this.featureService.extractServicesFor(feature);
    this.linkedServices = genericResourceToLinkItem(services, (value: GenericLinkedResource) => (router: Router) => this.openModifyServiceDialog(value));

    this.form = new FormGroup({
      name: new FormControl(feature.name, [Validators.required]),
      guiZone: new FormControl(feature._links.gui_zone.href, [Validators.required]),
    });
  }

  private refreshResource() {
    this.currentResourceService.refresh();
  }

  onSubmit() {
    const formValues = this.form.value;
    const prevGuiZone = this.feature._links.gui_zone.href;
    this.onFormSubmit.emit({
        resourceUri: this.resourceUri,
        formData: formValues,
        success: (result: any) => this.refreshGuiZone(prevGuiZone),
        failure: () => {},
      },
    );
  }

  onDelete($event) {
    this.deleteResource.emit({
      ref: this.resourceUri,
      success: () => this.refreshGuiZone(),
      failure: () => {},
    });
  }

  onUpdate($event) {
    this.updateService.update(
      () => this.featureService.updateFromFeature(this.feature.id),
      () => this.refreshResource(),
      'project feature');
  }

  private refreshGuiZone(prevGuiZone = null) {
    const guiZone = this.feature._links.gui_zone.href;
    const guiZoneService = this.genericService.findServiceForResource(guiZone);
    guiZoneService.refresh();
    if (prevGuiZone !== null && prevGuiZone != guiZone) {
      const prevGuiZoneService = this.genericService.findServiceForResource(prevGuiZone);
      prevGuiZoneService.refresh();
    }
  }

  private openModifyUnitDialog(unitResource: GenericLinkedResource) {
    this.openDialog(unitResource, (unit) => this.unitModalFactory(unit));
  }

  private openModifyServiceDialog(serviceResource: GenericLinkedResource) {
    this.openDialog(serviceResource, (service) => this.serviceModalFactory(service));
  }

  private serviceModalFactory(service) {
    const compRef = this.modalService.open(ModifyServiceComponent);
    compRef.componentInstance.serviceEntity = service;
    compRef.componentInstance.enableDelete = false;
    compRef.componentInstance.afterUpdate.subscribe((updatedService) => this.refreshResource());
    return compRef;
  }

  private unitModalFactory(unit) {
    const compRef = this.modalService.open(ModifyUnitComponent);
    compRef.componentInstance.unit = unit;
    compRef.componentInstance.enableDelete = false;
    compRef.componentInstance.afterUpdate.subscribe((updatedUnit) => this.refreshResource());
    return compRef;
  }

  private openDialog(resource: GenericLinkedResource, componentFactory) {
    let compRef;
    const onFullfill = () => {
      resourceHandler.refresh();
      this.unitSubscription.unsubscribe();
    };

    const onError = () => {
      this.unitSubscription.unsubscribe();
    };

    const resourceHandler = this
      .genericService
      .findServiceForResource(resource.href);

    this.unitSubscription = resourceHandler
      .loadAndGet()
      .subscribe((item) => {
        if (compRef) {
          compRef.close();
          compRef = null;
        }

        if (isNullOrUndefined(item)) {
          compRef = null;
        } else {
          compRef = componentFactory(item);
          compRef.result.then(onFullfill, onError);
        }
      });
  }

  getEntitySchemaTypeRef() {
    return EntitySchemaTypeRef;
  }

  private updateGuiZoneSelectItems(project) {
    this.guiZoneSelectItems = [];
    extractDataFromModel(project, 'gui_zones').forEach((guiZone) => {
      this.guiZoneSelectItems.push({
        value: guiZone.href,
        label: guiZone.label
      });
    });
  }

  navigateToFeature(href: any) {
    const id = href.substring(href.lastIndexOf('/') + 1);
    this.router.navigate(['features', id, 'properties']).then(() => console.log('succcess'));
  }
}



// WEBPACK FOOTER //
// /agent/workspace/CUS-LHT_NICE_nto_nto_CAT_release@2/app/Resources/Public/src/app/modules/project-details/components/landing-pages/project-features-landing/project-features-landing.component.ts